import { Fragment } from 'prosemirror-model';
import { Selection, TextSelection, NodeSelection, AllSelection, } from 'prosemirror-state';
import { toggleMark } from 'prosemirror-commands';
import { GapCursor } from './gapcursor/index.js';
import { getNodeType, isNodeActive } from './utils/node.js';
import { getMarkType, getMarksBetween } from './utils/mark.js';
import { getSchemaType, belongsTo } from './utils/schema-type.js';
import { deprecate, renameFnWarn } from './utils/deprecate.js';
import { SchemaType } from './constants.js';
import { wrapIn, unWrap } from './commands/wrapIn.js';
import { wrapLines } from './commands/wrapLines.js';
/**
 * 所有针对 selection 或 selection 内容进行的操作集合
 */
export class SelectionCommands {
    constructor(view, htmlSerializer) {
        this.view = view;
        this.htmlSerializer = htmlSerializer;
        /** @deprecated use addMark */
        this.addInline = deprecate(this.addMark, renameFnWarn('addInline'));
        /** @deprecated use removeMark */
        this.removeInline = deprecate(this.removeMark, renameFnWarn('removeInline'));
        /** @deprecated use removeMarkExcluding */
        this.removeInlineExcluding = deprecate(this.removeMarkExcluding, renameFnWarn('removeInlineExcluding'));
        /** @deprecated use toggleMark */
        this.toggleInline = deprecate(this.toggleMark, renameFnWarn('toggleInline'));
        /** @deprecated use hasMark */
        this.hasInline = deprecate(this.hasMark, renameFnWarn('hasInline'));
        /** @deprecated use getMarkAttrs */
        this.getInlineAttrs = deprecate(this.getMarkAttrs, renameFnWarn('getInlineAttrs'));
        /** @deprecated use replaceWithNode */
        this.setBlock = deprecate(this.replaceWithNode, renameFnWarn('setBlock', 'replaceWithNode'));
        /** @deprecated use convertToNode */
        this.convertToBlock = deprecate(this.convertToNode, renameFnWarn('convertToBlock', 'convertToNode'));
        /** @deprecated use replaceWithNodes */
        this.replaceWithBlockInstances = deprecate(this.replaceWithNodes, renameFnWarn('replaceWithBlockInstances', 'replaceWithNodes'));
        /** @deprecated use updateNodeAttr */
        this.updateBlockAttr = deprecate(this.updateNodeAttr, renameFnWarn('updateBlockAttr', 'updateNodeAttr'));
        /** @deprecated use getNodeAttr */
        this.getBlockAttr = deprecate(this.getNodeAttr, renameFnWarn('getBlockAttr', 'getNodeAttr'));
        this.dispatch = (tr) => {
            this.view.dispatch(tr);
            return this;
        };
        [
            'from',
            'to',
            'empty',
            'anchor',
            'head',
            '$from',
            '$to',
            '$anchor',
            '$head',
            'ranges',
        ].forEach((key) => {
            Object.defineProperty(this, key, {
                get() {
                    return view.state.selection[key];
                },
            });
        });
    }
    /**
     * 将 selection 设置到某个数值范围
     */
    set(from, to, bias) {
        const { tr } = this.state;
        const empty = typeof to !== 'number' || to === from;
        const $from = this.state.doc.resolve(from);
        if (empty && GapCursor.valid($from)) {
            tr.setSelection(new GapCursor($from));
        }
        else if (empty) {
            tr.setSelection(Selection.near($from, bias));
        }
        else {
            tr.setSelection(TextSelection.create(this.state.doc, from, to !== null && to !== void 0 ? to : from));
        }
        return this.dispatch(tr.scrollIntoView());
    }
    /** 全选 */
    setAll() {
        const { tr, doc } = this.state;
        tr.setSelection(new AllSelection(doc));
        return this.dispatch(tr.scrollIntoView());
    }
    /** 选中某个节点 */
    setNode(pos) {
        const { tr, doc } = this.state;
        tr.setSelection(new NodeSelection(doc.resolve(pos)));
        return this.dispatch(tr.scrollIntoView());
    }
    get type() {
        const { raw } = this;
        if (raw instanceof NodeSelection) {
            return 'node';
        }
        if (raw instanceof GapCursor) {
            return 'gap';
        }
        return raw.toJSON().type || 'text';
    }
    get raw() {
        return this.state.selection;
    }
    scrollIntoView() {
        const { tr } = this.state;
        this.dispatch(tr.scrollIntoView());
        return this;
    }
    /** 删除选中的所有内容 */
    delete() {
        const { tr } = this.state;
        return this.dispatch(tr.deleteSelection());
    }
    /** 获取或替换选中的文字 */
    text(content) {
        if (typeof content === 'undefined') {
            const { doc, selection } = this.state;
            return doc.textBetween(selection.from, selection.to);
        }
        const { tr } = this.state;
        this.dispatch(tr.insertText(content));
        return content;
    }
    /**
     * 获取或替换选中内容的 HTML
     */
    html(newHTML, fit = false) {
        const { selection } = this.state;
        if (typeof newHTML === 'undefined') {
            return this.htmlSerializer.htmlFromSlice(selection.content());
        }
        const { tr } = this.state;
        const slice = this.htmlSerializer.sliceFromHTML(newHTML);
        if (fit) {
            tr.replace(selection.from, selection.to, slice);
        }
        else {
            tr.replaceWith(selection.from, selection.to, slice.content);
        }
        this.dispatch(tr);
        return this.htmlSerializer.htmlFromSlice(slice);
    }
    /** 添加行内样式 */
    addMark(plugOrName, attrs) {
        const { selection, tr, schema } = this.state;
        const markType = getMarkType(plugOrName, schema);
        if (!markType) {
            return this;
        }
        const mark = markType.create(attrs);
        if (selection.empty) {
            tr.addStoredMark(mark);
        }
        else {
            selection.ranges.forEach((range) => {
                tr.addMark(range.$from.pos, range.$to.pos, mark);
            });
        }
        return this.dispatch(tr);
    }
    /** 消除行内样式 */
    removeMark(plugOrName) {
        const { selection, tr, schema } = this.state;
        const markType = plugOrName ? getMarkType(plugOrName, schema) : undefined;
        if (selection.empty) {
            if (markType) {
                tr.removeStoredMark(markType);
                this.dispatch(tr);
            }
            else {
                this.dispatch(tr.setStoredMarks([]));
            }
        }
        else {
            selection.ranges.forEach((range) => {
                tr.removeMark(range.$from.pos, range.$to.pos, markType);
            });
            this.dispatch(tr);
        }
        return this;
    }
    /**
     * 清除白名单以外的所有行内样式
     */
    removeMarkExcluding(plugOrNames) {
        if (!plugOrNames.length) {
            return this.removeMark();
        }
        const { selection, tr } = this.state;
        const excludeNames = plugOrNames.map((plug) => typeof plug === 'string' ? plug : plug.name);
        if (selection.empty) {
            const newStoredMarks = (tr.storedMarks || selection.$head.marks()).filter((mark) => belongsTo(mark, excludeNames));
            tr.setStoredMarks(newStoredMarks);
            this.dispatch(tr);
        }
        else {
            selection.ranges.forEach((range) => {
                const from = range.$from.pos;
                const to = range.$to.pos;
                const marks = getMarksBetween(from, to, tr.doc).map((range) => range.mark);
                marks.forEach((mark) => {
                    if (!belongsTo(mark, excludeNames)) {
                        tr.removeMark(from, to, mark);
                    }
                });
            });
            this.dispatch(tr);
        }
        return this;
    }
    /**
     * 切换指定行内样式的开关
     */
    toggleMark(plugOrName) {
        const markType = getMarkType(plugOrName, this.state.schema);
        if (markType) {
            toggleMark(markType)(this.state, this.dispatch);
        }
    }
    /**
     * 判断当前位置是否可以切换行内样式，常用于判断工具栏按钮是否可用
     */
    canToggleMark(plugOrName) {
        const markType = getMarkType(plugOrName, this.state.schema);
        return markType ? toggleMark(markType)(this.state) : false;
    }
    hasMark(plugOrName) {
        const { state } = this.view;
        const { selection } = state;
        const markType = getMarkType(plugOrName, this.state.schema);
        if (!markType) {
            return false;
        }
        if (selection.empty) {
            return (Array.isArray(state.storedMarks)
                ? state.storedMarks
                : selection.$from.marks()).some((mark) => mark.type === markType);
        }
        return state.doc.rangeHasMark(selection.from, selection.to, markType);
    }
    /**
     * 获取指定行内样式的属性
     */
    getMarkAttrs(plugOrName) {
        const pluginName = typeof plugOrName === 'string' ? plugOrName : plugOrName.name;
        const { selection: { from, to, empty }, } = this.state;
        const marks = [];
        if (empty) {
            if (this.state.storedMarks) {
                marks.push(...this.state.storedMarks);
            }
            marks.push(...this.state.selection.$head.marks());
        }
        else {
            this.state.doc.nodesBetween(from, to, (node) => {
                marks.push(...node.marks);
            });
        }
        const mark = marks.find((markItem) => markItem.type.name === pluginName);
        if (!mark) {
            return {};
        }
        return { ...mark.attrs };
    }
    /**
     * 设置为指定块，会替换掉目前选中的内容
     */
    replaceWithNode(plugOrName, attrs) {
        const { tr, schema } = this.state;
        const nodeType = getNodeType(plugOrName, schema);
        const node = nodeType.createAndFill(attrs);
        if (node) {
            // replaceSelectionWith 的第二个参数 inheritMarks 会导致 prosemirror 创建一个
            // 新 node，干扰我们后续的判断，所以仅在必要时使用
            tr.replaceSelectionWith(node, node.isInline);
            // prosemirror 倾向于把光标放置于后面的 Node 上，这里尝试设置回刚插入的 Node
            // 不使用旧 selection 坐标或 mapping 的原因是新 Node 不一定在旧 selection 的位置，
            // 如在 codeBlock 末尾插入分隔线
            if (tr.selection instanceof NodeSelection &&
                tr.selection.$from.nodeBefore === node) {
                tr.setSelection(NodeSelection.create(tr.doc, tr.selection.from - node.nodeSize));
            }
        }
        return this.dispatch(tr);
    }
    /**
     * 把所有满足 predicate 的目标块
     * 设置成为另一种（或同种）块，会尽量保留原来的 attrs
     */
    convertToNode(plugOrName, attrs = {}, predicate = (node) => node.isTextblock) {
        const { doc, tr, selection: { ranges }, schema, } = this.state;
        const nodeType = getNodeType(plugOrName, schema);
        ranges.forEach((range) => {
            const from = range.$from.pos;
            const to = range.$to.pos;
            doc.nodesBetween(from, to, (node, pos) => {
                if (!predicate(node)) {
                    return;
                }
                tr.setBlockType(pos, pos + node.nodeSize, nodeType, {
                    ...node.attrs,
                    ...attrs,
                });
                return !node.isBlock;
            });
        });
        this.dispatch(tr);
        return this;
    }
    /**
     * 切换包裹块的开关
     */
    toggleWrappingBlock(plugOrName, wrap = wrapIn, unwrap = unWrap) {
        const { state, dispatch } = this.view;
        const nodeType = getNodeType(plugOrName, this.state.schema);
        if (!unwrap(nodeType)(state, dispatch)) {
            wrap(nodeType)(state, dispatch);
        }
    }
    /**
     * 替换为复数个指定块，每个块的属性可单独设置
     */
    replaceWithNodes(plugOrName, attrsList) {
        const { selection, tr, schema } = this.state;
        const nodeType = getNodeType(plugOrName, schema);
        const nodes = attrsList.map((attrs) => nodeType.create(attrs));
        tr.replaceWith(selection.from, selection.to, Fragment.fromArray(nodes));
        this.dispatch(tr);
        return nodes;
    }
    updateNodeAttr(plugNames, attrName, value) {
        const { doc, tr, selection: { ranges }, } = this.state;
        const attrNameValues = typeof attrName === 'string' ? [{ name: attrName, value }] : attrName;
        ranges.forEach((range) => {
            const from = range.$from.pos;
            const to = range.$to.pos;
            doc.nodesBetween(from, to, (node, pos) => {
                if (belongsTo(node, plugNames)) {
                    let set = false;
                    const attrs = {};
                    attrNameValues.forEach(({ name, value }) => {
                        if (Object.prototype.hasOwnProperty.call(node.attrs, name)) {
                            set = true;
                            attrs[name] = value;
                        }
                    });
                    if (set) {
                        tr.setNodeMarkup(pos, undefined, {
                            ...node.attrs,
                            ...attrs,
                        });
                    }
                }
            });
        });
        this.dispatch(tr);
    }
    /**
     * 获取（第一个）符合类型节点的某项属性
     */
    getNodeAttr(plugNames, attrName) {
        const { doc, selection: { ranges }, } = this.state;
        let ret = null;
        for (const range of ranges) {
            const from = range.$from.pos;
            const to = range.$to.pos;
            doc.nodesBetween(from, to, (node) => {
                if (ret == null &&
                    belongsTo(node, plugNames) &&
                    Object.prototype.hasOwnProperty.call(node.attrs, attrName)) {
                    ret = node.attrs[attrName];
                    return false;
                }
                return;
            });
            if (ret) {
                break;
            }
        }
        return ret;
    }
    hasNode(plugNames) {
        const { doc, selection: { ranges }, } = this.state;
        let ret = false;
        for (const range of ranges) {
            const from = range.$from.pos;
            const to = range.$to.pos;
            doc.nodesBetween(from, to, (node) => {
                if (ret === false && belongsTo(node, plugNames)) {
                    ret = true;
                }
                return;
            });
            if (ret) {
                break;
            }
        }
        return ret;
    }
    /**
     * 将文字块重置为默认的段落类型
     */
    resetTextBlock(name = 'paragraph') {
        const { tr, selection: { ranges }, schema, } = this.state;
        const type = schema.nodes[name];
        ranges.forEach((range) => {
            const from = range.$from.pos;
            const to = range.$to.pos;
            tr.doc.nodesBetween(from, to, (node, pos, parent, index) => {
                if (node.isTextblock) {
                    // 需要同时满足原父级允许新类型+新类型允许原内容
                    const canChangeType = (parent === null || parent === void 0 ? void 0 : parent.canReplaceWith(index, index + node.nodeSize, type)) &&
                        type.validContent(node.content);
                    tr.setNodeMarkup(pos, canChangeType ? type : undefined);
                }
            });
        });
        this.dispatch(tr);
    }
    /**
     * 判断指定的块或行内样式是否生效
     */
    isActive(plug) {
        var _a;
        const { state } = this.view;
        const { schema } = state;
        switch (getSchemaType((_a = plug.spec) === null || _a === void 0 ? void 0 : _a.type)) {
            case SchemaType.MARK: {
                return this.hasMark(plug);
            }
            case SchemaType.NODE: {
                const nodeType = getNodeType(plug, schema);
                if (nodeType.isTextblock) {
                    return isNodeActive(state, nodeType);
                }
                return unWrap(nodeType)(state);
            }
        }
    }
    /**
     * 把选中的所有行包裹在一个文字块内，如代码块
     */
    wrapLines(plugOrName, attrs = {}, joiner = '\n') {
        const nodeType = getNodeType(plugOrName, this.state.schema);
        return wrapLines(nodeType, attrs, joiner)(this.state, this.dispatch);
    }
    /**
     * 将文字块中的内容提出，按换行替换为 br
     */
    unwrapLines() {
        const { tr, selection: { ranges }, schema, } = this.state;
        const nodeType = getNodeType('paragraph', schema);
        ranges.forEach((range) => {
            const from = range.$from.pos;
            const to = range.$to.pos;
            tr.doc.nodesBetween(from, to, (node, pos) => {
                if (!node.isTextblock) {
                    return;
                }
                tr.setBlockType(pos, pos + node.nodeSize, nodeType);
                let replacedCount = 0;
                while (tr.doc.nodeAt(pos).textContent.includes('\n')) {
                    const newlineIndex = tr.doc.nodeAt(pos).textContent.indexOf('\n') + 1 + replacedCount;
                    tr.replaceWith(pos + newlineIndex, pos + newlineIndex + 1, schema.nodes.hard_break.create());
                    replacedCount++;
                }
                return !node.isBlock;
            });
        });
        this.dispatch(tr);
    }
    /**
     * 如果当前光标位于 gap，那么在这里创建一个空段落
     */
    insertEmptyParagraphIfGap() {
        const { selection } = this.state;
        if (!(selection instanceof GapCursor)) {
            return this;
        }
        const { tr, schema } = this.state;
        const nodeType = getNodeType('paragraph', schema);
        tr.insert(selection.from, nodeType.create());
        tr.setSelection(TextSelection.create(tr.doc, selection.from + 1, selection.from + 1));
        return this.dispatch(tr);
    }
    /**
     * 如果当前正选中了满足条件的块，那么将光标移动到附近的 gap
     */
    gapIfANode(predicate = (node) => node.isLeaf, bias = 1) {
        const { selection, doc } = this.state;
        if (selection instanceof NodeSelection && predicate(selection.node)) {
            this.setRaw(new GapCursor(doc.resolve(bias < 0 ? selection.from : selection.to)));
        }
        return this;
    }
    /**
     * 判断选择区域是否是指定节点
     */
    isNode(predicate = (node) => node.isLeaf) {
        const { selection } = this.state;
        return selection instanceof NodeSelection && predicate(selection.node);
    }
    setRaw(sel) {
        this.dispatch(this.state.tr.setSelection(sel).scrollIntoView());
    }
    get state() {
        return this.view.state;
    }
}
//# sourceMappingURL=Selection.js.map